import { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../supabase'
import { montant } from './format'
import {
  t, Stagger, Panel, PanelHead, PanelTitle, Btn, IconBtn, Input, Textarea,
  Label, Field, Row, Cluster, Pill, Muted, DefList, DefRow, BackLink,
  Loading, Empty, useUI,
} from './ui'
import { ecrire } from './erreurs'
import CoutLivraison from './CoutLivraison'
import { decouper, synchroniserCommande } from './tournees'
import { ORDER_TYPES, ORDER_STATUS, WORKFLOW_ORDER, echeance, fmtLong, calcLigne, totauxDe, camionsDe, CAPACITE_CAMION } from './carnetUtils'

/* ── En-tête ── */
const Hero = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-left: 3px solid ${(p) => p.$accent};
  border-radius: ${t.r};
  padding: 18px 20px;
  margin-bottom: 14px;
  @media (max-width: 700px) { padding: 15px 16px; }
`
const HeroTop = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; flex-wrap: wrap;
`
const HeroRef = styled.div`
  display: inline-block;
  font-family: ${t.mono}; font-size: 0.655rem; letter-spacing: 0.09em;
  color: ${t.gold}; margin-bottom: 6px;
  background: ${t.goldWash}; padding: 2px 7px; border-radius: ${t.rXs};
`
const HeroName = styled.h1`
  font-family: ${t.serif}; font-size: 1.3rem; font-weight: 660;
  letter-spacing: -0.036em; color: ${t.text}; margin: 0;
  @media (max-width: 700px) { font-size: 1.12rem; }
`
const HeroMeta = styled.div`
  font-family: ${t.font}; font-size: 0.765rem; color: ${t.textSoft}; margin-top: 6px;
  display: flex; flex-wrap: wrap; gap: 4px 14px;
  a { color: ${t.gold}; text-decoration: none; &:hover { text-decoration: underline; } }
`

/* ── Statut ── */
const StatusPick = styled.button`
  text-align: left;
  padding: 9px 11px;
  border-radius: ${t.rSm};
  cursor: pointer;
  font-family: ${t.font};
  transition: background 0.14s, border-color 0.14s, color 0.14s;
  background: ${(p) => (p.$on ? p.$accent : t.surface)};
  color: ${(p) => (p.$on ? '#fff' : t.textSoft)};
  border: 1px solid ${(p) => (p.$on ? p.$accent : t.line)};
  font-size: 0.745rem; font-weight: ${(p) => (p.$on ? 570 : 480)};
  letter-spacing: -0.01em; line-height: 1.3;
  &:hover { border-color: ${(p) => p.$accent}; color: ${(p) => (p.$on ? '#fff' : p.$accent)}; }
`
const TypeRow = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`
const TypePick = styled.button`
  padding: 9px 11px; border-radius: ${t.rSm}; cursor: pointer;
  font-family: ${t.font}; font-size: 0.75rem; letter-spacing: -0.01em;
  transition: background 0.14s, border-color 0.14s, color 0.14s;
  font-weight: ${(p) => (p.$on ? 600 : 480)};
  background: ${(p) => (!p.$on ? t.surface : p.$tone === 'blue' ? t.blueWash : p.$tone === 'amber' ? t.amberWash : t.surfaceSunk)};
  color: ${(p) => (!p.$on ? t.textSoft : p.$tone === 'blue' ? t.blue : p.$tone === 'amber' ? t.amber : t.text)};
  border: 1px solid ${(p) => (!p.$on ? t.line : p.$tone === 'blue' ? t.blue : p.$tone === 'amber' ? t.amber : t.textFaint)};
  &:hover { border-color: ${t.textFaint}; }
`
const Steps = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px;
  @media (max-width: 700px) { grid-template-columns: repeat(2, 1fr); }
`

/* ── Lignes ── */
const LigneBox = styled.div`
  background: ${t.surfaceAlt};
  border: 1px solid ${t.line};
  border-radius: ${t.rSm};
  padding: 12px 14px;
  margin-bottom: 8px;
`
const LigneName = styled.div`
  font-family: ${t.font}; font-weight: 620; font-size: 0.88rem;
  letter-spacing: -0.016em; color: ${t.text}; margin-bottom: 9px;
`
const MiniGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(92px, 1fr)); gap: 9px;
`
const MiniLabel = styled.div`
  font-size: 0.62rem; color: ${t.textFaint}; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.075em; margin-bottom: 2px;
`
const MiniVal = styled.div`
  font-family: ${t.font}; font-size: 0.83rem; font-weight: 560;
  color: ${(p) => p.$c || t.text}; font-variant-numeric: tabular-nums;
`
/* Sous-total recalculé en direct pendant la saisie */
const Recalcul = styled.div`
  display: flex; justify-content: flex-end; align-items: baseline; gap: 7px;
  margin-top: 10px; padding-top: 9px;
  border-top: 1px dashed ${t.line};
  font-family: ${t.font}; font-size: 0.73rem; color: ${t.textFaint};
  b {
    font-size: 0.88rem; font-weight: 640; color: ${t.green};
    font-variant-numeric: tabular-nums;
  }
`
const TotalBanner = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  background: ${(p) => (p.$blue ? t.blueWash : t.goldWash)};
  border: 1px solid ${(p) => (p.$blue ? '#D3E4F3' : '#CFE2DC')};
  border-radius: ${t.rSm};
  padding: 11px 15px;
  font-family: ${t.font};
  margin-top: 12px;
  span:first-child {
    font-size: 0.625rem; font-weight: 600; letter-spacing: 0.075em;
    text-transform: uppercase; color: ${(p) => (p.$blue ? t.blue : t.gold)}; opacity: 0.75;
  }
  span:last-child {
    font-family: ${t.serif}; font-size: 0.95rem; font-weight: 640; letter-spacing: -0.026em;
    font-variant-numeric: tabular-nums; color: ${(p) => (p.$blue ? t.blue : t.gold)};
  }
`
const Echeance = styled.div`
  display: inline-block;
  font-family: ${t.font}; font-size: 0.7rem; font-weight: 620;
  padding: 2px 8px; border-radius: ${t.rXs};
  background: ${(p) => (p.$tone === 'late' ? t.redWash : p.$tone === 'urgent' ? t.amberWash : p.$tone === 'soon' ? t.blueWash : t.surfaceSunk)};
  color: ${(p) => (p.$tone === 'late' ? t.red : p.$tone === 'urgent' ? t.amber : p.$tone === 'soon' ? t.blue : t.textFaint)};
`

/* Adresse de livraison reconstituée depuis la fiche client. Sur un
   devis elle vient du formulaire ; ici, c'est le client qui la porte. */
function destinationClient(c) {
  if (!c) return null
  const parts = [c.adresse, c.localite, c.code_postal, c.canton].filter((v) => v && String(v).trim())
  return parts.length ? `${parts.join(', ')}, Suisse` : null
}

const Tournee = styled.div`
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto auto;
  gap: 11px; align-items: center;
  padding: 9px 11px; margin-bottom: 7px;
  border: 1px solid ${t.line}; border-radius: ${t.rSm};
  background: ${(p) => (p.$livree ? t.surfaceSunk : t.surface)};
  opacity: ${(p) => (p.$livree ? 0.75 : 1)};

  @media (max-width: 620px) {
    grid-template-columns: 24px minmax(0, 1fr);
    row-gap: 8px;
  }
`
const TourneeRang = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; border-radius: 50%;
  background: ${t.goldWash}; color: ${t.gold};
  font-family: ${t.font}; font-size: 0.72rem; font-weight: 650;
`
const TourneeNom = styled.div`
  font-family: ${t.font}; font-size: 0.84rem; font-weight: 600; color: ${t.text};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`
const TourneeInfo = styled.div`
  font-family: ${t.font}; font-size: 0.73rem; color: ${t.textFaint};
  font-variant-numeric: tabular-nums;
`

export default function CarnetDetail({ onValidationAlert }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast, confirm } = useUI()

  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [dateLiv, setDateLiv] = useState('')
  const [tournees, setTournees] = useState([])
  const [notesAdmin, setNotesAdmin] = useState('')
  const [editLignes, setEditLignes] = useState(null) // null = lecture seule
  const [editCamions, setEditCamions] = useState('') // '' = calcul auto
  const [savingLignes, setSavingLignes] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('orders')
      .select('*, clients(name, email, phone, canton, adresse, code_postal, localite)')
      .eq('id', id).maybeSingle()
    if (!data) { setNotFound(true); setLoading(false); return }
    setOrder(data)
    setDateLiv(data.date_livraison || '')

    const { data: tr } = await supabase.from('tournees')
      .select('*').eq('order_id', id).order('rang')
    setTournees(tr || [])
    setNotesAdmin(data.notes_admin || '')
    setLoading(false)
  }, [id])

  useEffect(() => { load() }, [load])

  /* Le client peut valider sa commande pendant que la fiche est ouverte */
  useEffect(() => {
    const channel = supabase
      .channel(`order-${id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'orders', filter: `id=eq.${id}` },
        (payload) => {
          if (payload.new.status === 'validated' && payload.old.status !== 'validated') onValidationAlert?.()
          setOrder((prev) => (prev ? { ...prev, ...payload.new } : prev))
        })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateOrder = async (updates) => {
    const ok = await ecrire(
      supabase.from('orders').update(updates).eq('id', id),
      toast, 'mise à jour de la commande',
    )
    // Sans ce garde-fou, un rejet de la base passait pour un succès
    if (!ok) return
    if (updates.status === 'validated' || updates.status === 'delivered') {
      if (order?.client_id) await supabase.from('clients').update({ type: 'fidele' }).eq('id', order.client_id)
    }
    setOrder((prev) => ({ ...prev, ...updates }))
    toast('Commande mise à jour')
  }

  /* ── Édition des lignes (tarif, poids, taxe, camions) ── */
  const ouvrirEdition = () => {
    setEditCamions(order.nb_camions_total != null ? String(order.nb_camions_total) : '')
    setEditLignes((order.lignes || []).map((l) => ({
      produit: l.produit,
      poids_t: l.poids_t ?? '',
      bottes: l.bottes ?? '',
      prix_100kg: l.prix_100kg ?? '',
      taxe_100kg: l.taxe_100kg ?? '0',
    })))
  }

  const majLigne = (i, champ, val) =>
    setEditLignes((prev) => prev.map((l, idx) => (idx === i ? { ...l, [champ]: val } : l)))

  const enregistrerLignes = async () => {
    setSavingLignes(true)
    const { lignes: data, poids_total_t, ca_total_chf } = totauxDe(editLignes)
    const nb_camions_total = editCamions === '' ? null : parseInt(editCamions, 10)
    const maj = { lignes: data, poids_total_t, ca_total_chf, nb_camions_total }
    const ok = await ecrire(supabase.from('orders').update(maj).eq('id', id), toast, 'enregistrement')
    setSavingLignes(false)
    if (!ok) return
    setOrder((prev) => ({ ...prev, ...maj }))
    setEditLignes(null)
    toast('Commande mise à jour')
  }

  /* Nature du document. Passer un devis en pro forma le rend ferme :
     on avance aussi le statut, sinon il resterait « Devis envoyé »
     alors que la commande est engagée. */
  const changeType = async (type) => {
    if (type === order.type) return
    const maj = { type }
    if (type !== 'devis' && order.status === 'sent') maj.status = 'validated'
    const ok = await ecrire(supabase.from('orders').update(maj).eq('id', id), toast, 'changement de nature')
    if (!ok) return
    if (type !== 'devis' && order.client_id) {
      await supabase.from('clients').update({ type: 'fidele' }).eq('id', order.client_id)
    }
    setOrder((prev) => ({ ...prev, ...maj }))
    toast(maj.status ? `${ORDER_TYPES[type].label} — commande passée en ferme` : ORDER_TYPES[type].label)
  }

  const changeStatus = (key) => {
    if (key === order.status) return
    // Marquer livré ouvre la page de clôture : poids, prix et quantités réels
    if (key === 'delivered') {
      navigate(`/admin/carnet/${id}/livraison`)
      return
    }
    updateOrder({ status: key })
  }

  const remove = async () => {
    const ok = await confirm({
      icon: '🗑️', tone: 'red',
      title: 'Supprimer cette commande ?',
      body: <>La commande de <strong>{order.clients?.name || 'ce client'}</strong> sera retirée du carnet. Cette action est irréversible.</>,
      confirmLabel: 'Supprimer',
    })
    if (!ok) return
    await supabase.from('orders').delete().eq('id', id)
    toast('Commande supprimée du carnet')
    navigate('/admin/carnet')
  }

  /* Découpe la commande en autant de tournées que de camions. On ne
     propose la découpe que si la liste est vide : la régénérer
     effacerait des dates déjà posées. */
  const creerTournees = async () => {
    /* Le nombre saisi sur la commande fait foi : c'est celui qu'annonce
       le bouton, et celui que tu as éventuellement corrigé à la main. */
    const proposees = decouper(order.lignes, { total: camionsDe(order) })
      .map((t) => ({ ...t, order_id: id }))
    if (proposees.length === 0) return toast('Aucune marchandise à répartir', 'error')
    const ok = await ecrire(
      supabase.from('tournees').insert(proposees), toast, 'création des tournées',
    )
    if (!ok) return
    toast(`${proposees.length} tournée(s) créée(s)`)
    load()
  }

  const majTournee = async (tourneeId, champs) => {
    const ok = await ecrire(
      supabase.from('tournees').update({ ...champs, updated_at: new Date().toISOString() }).eq('id', tourneeId),
      toast, 'mise à jour de la tournée',
    )
    if (!ok) return
    /* La commande recopie la date de sa première tournée : sans cette
       synchronisation, le carnet et les statistiques resteraient sur une
       date périmée. */
    await synchroniserCommande(id)
    load()
  }

  const supprimerTournees = async () => {
    const ok = await confirm({
      icon: '🗑️', tone: 'red',
      title: 'Revenir à une seule date ?',
      body: <>Les {tournees.length} tournées et leurs dates seront supprimées. La commande
        retrouvera une date de livraison unique.</>,
      confirmLabel: 'Supprimer les tournées',
    })
    if (!ok) return
    await supabase.from('tournees').delete().eq('order_id', id)
    toast('Tournées supprimées')
    load()
  }

  if (loading) {
    return (
      <>
        <BackLink onClick={() => navigate('/admin/carnet')}>← Retour au carnet</BackLink>
        <Loading rows={3} h="120px" />
      </>
    )
  }
  if (notFound) {
    return (
      <>
        <BackLink onClick={() => navigate('/admin/carnet')}>← Retour au carnet</BackLink>
        <Empty icon="🔍" title="Commande introuvable">Cette commande a peut-être été supprimée.</Empty>
      </>
    )
  }

  const st = ORDER_STATUS[order.status] || ORDER_STATUS.sent
  const lignes = order.lignes || []
  const nbCamions = camionsDe(order)
  const ech = order.status === 'scheduled' ? echeance(order.date_livraison) : null
  const ecart = order.ca_reel_chf ? order.ca_reel_chf - (order.ca_total_chf || 0) : null

  return (
    <>
      <BackLink onClick={() => navigate('/admin/carnet')}>← Retour au carnet</BackLink>

      <Hero $accent={st.accent}>
        <HeroTop>
          <div style={{ minWidth: 0 }}>
            <HeroRef>#{order.id.slice(0, 8).toUpperCase()}</HeroRef>
            <HeroName>{order.clients?.name || '—'}</HeroName>
            <HeroMeta>
              {order.clients?.email && <a href={`mailto:${order.clients.email}`}>{order.clients.email}</a>}
              {order.clients?.phone && <a href={`tel:${order.clients.phone}`}>{order.clients.phone}</a>}
              {order.clients?.canton && <span>{order.clients.canton}</span>}
            </HeroMeta>
          </div>
          <Cluster $gap="8px">
            {order.type === 'facture' && <Pill $tone="blue">🧾 Facture</Pill>}
            <Pill $tone={st.tone} $lg>{st.label}</Pill>
            <IconBtn $sm onClick={remove} title="Supprimer la commande">🗑</IconBtn>
          </Cluster>
        </HeroTop>
      </Hero>

      <Row $cols="1fr 1fr" $gap="16px">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Nature du document */}
          <Stagger $i={0}>
            <Panel>
              <PanelHead>
                <PanelTitle $sm>Nature du document</PanelTitle>
                {order.type === 'devis' && <Muted style={{ fontSize: '0.72rem' }}>rien n'est encore engagé</Muted>}
              </PanelHead>
              <TypeRow>
                {Object.entries(ORDER_TYPES).map(([key, meta]) => (
                  <TypePick key={key} $on={order.type === key} $tone={meta.tone}
                    onClick={() => changeType(key)}>
                    {meta.label}
                  </TypePick>
                ))}
              </TypeRow>
            </Panel>
          </Stagger>

          {/* Statut */}
          <Stagger $i={1}>
            <Panel>
              <PanelHead><PanelTitle $sm>Avancement</PanelTitle></PanelHead>
              <Steps>
                {WORKFLOW_ORDER.map((key) => (
                  <StatusPick
                    key={key}
                    $on={order.status === key}
                    $accent={ORDER_STATUS[key].accent}
                    onClick={() => changeStatus(key)}
                  >
                    {ORDER_STATUS[key].label}
                  </StatusPick>
                ))}
              </Steps>
            </Panel>
          </Stagger>

          {/* Livraison */}
          <Stagger $i={1}>
            <Panel>
              <PanelHead>
                <PanelTitle $sm>🚛 Livraison</PanelTitle>
                {ech && <Echeance $tone={ech.tone}>{ech.texte}</Echeance>}
                {order.status === 'delivered' && (
                  <Btn $variant="ghost" $size="sm"
                    onClick={() => navigate(`/admin/carnet/${id}/livraison`)}>
                    ✏️ Modifier la clôture
                  </Btn>
                )}
              </PanelHead>
              {order.date_livraison && (
                <Muted style={{ marginBottom: 12 }}>
                  {order.status === 'delivered' ? 'Livrée le ' : 'Prévue le '}
                  <strong style={{ color: t.text }}>{fmtLong(order.date_livraison)}</strong>
                </Muted>
              )}
              {tournees.length === 0 ? (
                <>
                  <Row $cols="1fr auto" $gap="10px">
                    <Field $flush>
                      <Label>Date de livraison</Label>
                      <Input type="date" value={dateLiv} onChange={(e) => setDateLiv(e.target.value)} />
                    </Field>
                    <Field $flush style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <Btn $variant="gold" onClick={() => updateOrder({ date_livraison: dateLiv || null })}>
                        Enregistrer
                      </Btn>
                    </Field>
                  </Row>

                  {camionsDe(order) > 1 && (
                    <Muted style={{ fontSize: '0.76rem', marginTop: 12, display: 'block' }}>
                      Cette commande demande <strong style={{ color: t.text }}>{camionsDe(order)} camions</strong>.
                      Vous pouvez leur donner des dates distinctes.
                      <Btn $variant="ghost" $size="sm" style={{ marginLeft: 10 }} onClick={creerTournees}>
                        Répartir en {camionsDe(order)} tournées
                      </Btn>
                    </Muted>
                  )}
                </>
              ) : (
                <>
                  {tournees.map((tr) => (
                    <Tournee key={tr.id} $livree={tr.statut === 'livree'}>
                      <TourneeRang>{tr.rang}</TourneeRang>
                      <div style={{ minWidth: 0 }}>
                        <TourneeNom>{tr.produits || 'marchandise'}</TourneeNom>
                        <TourneeInfo>{tr.poids_t ? `${tr.poids_t} T` : 'tonnage libre'}</TourneeInfo>
                      </div>
                      <Input type="date" value={tr.date_livraison || ''}
                        onChange={(e) => majTournee(tr.id, { date_livraison: e.target.value || null })}
                        style={{ maxWidth: 168 }} />
                      <Btn $variant={tr.statut === 'livree' ? 'gold' : 'quiet'} $size="sm"
                        title={tr.statut === 'livree' ? 'Marquer comme à livrer' : 'Marquer comme livrée'}
                        onClick={() => majTournee(tr.id, { statut: tr.statut === 'livree' ? 'prevue' : 'livree' })}>
                        {tr.statut === 'livree' ? '📦 Livrée' : 'À livrer'}
                      </Btn>
                    </Tournee>
                  ))}
                  <Cluster $gap="9px" style={{ marginTop: 12 }}>
                    <Muted style={{ fontSize: '0.74rem', flex: 1 }}>
                      La commande porte la date de sa première tournée ; c'est elle qui
                      apparaît dans le carnet et les statistiques.
                    </Muted>
                    <Btn $variant="quiet" $size="sm" onClick={supprimerTournees}>
                      Revenir à une date unique
                    </Btn>
                  </Cluster>
                </>
              )}
            </Panel>
          </Stagger>

          {/* Notes */}
          <Stagger $i={2}>
            <Panel>
              <PanelHead><PanelTitle $sm>Notes internes</PanelTitle></PanelHead>
              <Field>
                <Textarea
                  value={notesAdmin}
                  onChange={(e) => setNotesAdmin(e.target.value)}
                  placeholder="Infos logistiques, contact chauffeur, remarques…"
                />
              </Field>
              <Btn $variant="ghost" onClick={() => updateOrder({ notes_admin: notesAdmin })}>
                💾 Enregistrer les notes
              </Btn>
            </Panel>
          </Stagger>

          {/* Actions */}
          {order.clients?.email && (
            <Stagger $i={3}>
              <Panel $pad="16px 20px">
                <Label>Contacter le client</Label>
                <Cluster $gap="9px">
                  <Btn $variant="ghost" $size="sm" as="a" href={`mailto:${order.clients.email}?subject=Livraison — achatpaille.com`}>
                    📧 Email
                  </Btn>
                  {order.clients.phone && (
                    <>
                      <Btn $variant="ghost" $size="sm" as="a" href={`tel:${order.clients.phone}`}>📞 Appeler</Btn>
                      <Btn $variant="ghost" $size="sm" as="a" target="_blank" rel="noopener noreferrer"
                        href={`https://wa.me/${order.clients.phone.replace(/\s+/g, '').replace('+', '')}`}>
                        💬 WhatsApp
                      </Btn>
                    </>
                  )}
                </Cluster>
              </Panel>
            </Stagger>
          )}
        </div>

        {/* Détail de la commande */}
        <div>
          <Stagger $i={1}>
            <Panel>
              <PanelHead>
                <PanelTitle>Détail de la commande</PanelTitle>
                {!editLignes && lignes.length > 0 && (
                  <Btn $variant="ghost" $size="sm" onClick={ouvrirEdition}>✏️ Modifier tarif et poids</Btn>
                )}
              </PanelHead>

              {editLignes ? (
                <>
                  {editLignes.map((l, i) => {
                    const douane = l.produit === 'Frais de douane'
                    const c = calcLigne(l)
                    return (
                      <LigneBox key={i}>
                        <LigneName>{l.produit}</LigneName>
                        <Row $cols={douane ? '1fr' : '1fr 1fr 1fr 1fr'} $gap="9px">
                          {douane ? (
                            <Field $flush>
                              <Label>Montant</Label>
                              <Input type="number" value={l.prix_100kg}
                                onChange={(e) => majLigne(i, 'prix_100kg', e.target.value)} />
                            </Field>
                          ) : (
                            <>
                              <Field $flush>
                                <Label>Poids (T)</Label>
                                <Input type="number" step="0.1" value={l.poids_t}
                                  onChange={(e) => majLigne(i, 'poids_t', e.target.value)} />
                              </Field>
                              <Field $flush>
                                <Label>Bottes</Label>
                                <Input type="number" value={l.bottes}
                                  onChange={(e) => majLigne(i, 'bottes', e.target.value)} placeholder="—" />
                              </Field>
                              <Field $flush>
                                <Label>Prix /100kg</Label>
                                <Input type="number" step="0.01" value={l.prix_100kg}
                                  onChange={(e) => majLigne(i, 'prix_100kg', e.target.value)} />
                              </Field>
                              <Field $flush>
                                <Label>Taxe /100kg</Label>
                                <Input type="number" step="0.01" value={l.taxe_100kg}
                                  onChange={(e) => majLigne(i, 'taxe_100kg', e.target.value)} />
                              </Field>
                            </>
                          )}
                        </Row>
                        <Recalcul>
                          Sous-total <b>{montant(c.total)}</b>
                        </Recalcul>
                      </LigneBox>
                    )
                  })}

                  <Field style={{ marginTop: 14 }}>
                    <Label>Camions pour toute la commande</Label>
                    <Row $cols="120px 1fr" $gap="10px">
                      <Input
                        type="number" min="0" value={editCamions}
                        onChange={(e) => setEditCamions(e.target.value)}
                        placeholder={String(totauxDe(editLignes).nb_camions)}
                      />
                      <Muted style={{ fontSize: '0.74rem', alignSelf: 'center' }}>
                        Vide = calcul automatique sur le poids total
                        ({totauxDe(editLignes).poids_total_t.toFixed(1)} T ÷ {CAPACITE_CAMION} = {totauxDe(editLignes).nb_camions}).
                        Un camion peut transporter plusieurs marchandises.
                      </Muted>
                    </Row>
                  </Field>

                  <TotalBanner>
                    <span>Nouveau total</span>
                    <span>
                      {montant(totauxDe(editLignes).ca_total_chf)} · {totauxDe(editLignes).poids_total_t.toFixed(1)} T ·{' '}
                      {editCamions === '' ? totauxDe(editLignes).nb_camions : editCamions} camion(s)
                    </span>
                  </TotalBanner>

                  <Cluster $gap="9px" style={{ marginTop: 12 }}>
                    <Btn $variant="gold" onClick={enregistrerLignes} disabled={savingLignes}>
                      {savingLignes ? 'Enregistrement…' : '💾 Enregistrer'}
                    </Btn>
                    <Btn $variant="ghost" onClick={() => setEditLignes(null)} disabled={savingLignes}>Annuler</Btn>
                  </Cluster>

                  {order.ca_reel_chf ? (
                    <Muted style={{ fontSize: '0.74rem', marginTop: 10 }}>
                      Le CA réel encaissé ({montant(order.ca_reel_chf)}) n'est pas modifié ici.
                    </Muted>
                  ) : null}
                </>
              ) : (
                <>
                  {lignes.map((l, i) => (
                    <LigneBox key={i}>
                      <LigneName>{l.produit}</LigneName>
                      <MiniGrid>
                        <div><MiniLabel>Poids</MiniLabel><MiniVal>{l.poids_t} T</MiniVal></div>
                        {l.bottes ? <div><MiniLabel>Bottes</MiniLabel><MiniVal>{l.bottes}</MiniVal></div> : null}
                        <div><MiniLabel>Prix</MiniLabel><MiniVal>{l.prix_100kg} CHF/100kg</MiniVal></div>
                        <div><MiniLabel>Sous-total</MiniLabel><MiniVal $c={t.green}>{montant(l.sous_total)}</MiniVal></div>
                      </MiniGrid>
                    </LigneBox>
                  ))}

                  <DefList style={{ marginTop: 12 }}>
                    <DefRow><dt>Poids total</dt><dd>{order.poids_total_t || 0} T</dd></DefRow>
                    <DefRow>
                      <dt>Camions</dt>
                      <dd>{nbCamions}{order.nb_camions_total == null ? ' (auto)' : ''}</dd>
                    </DefRow>
                  </DefList>

                  <TotalBanner>
                    <span>CA prévisionnel</span>
                    <span>{montant(order.ca_total_chf)}</span>
                  </TotalBanner>
                </>
              )}

              {order.ca_compta_chf != null ? (
                <TotalBanner $blue>
                  <span>Montant compta</span>
                  <span>{montant(order.ca_compta_chf)}</span>
                </TotalBanner>
              ) : null}

              {order.ca_reel_chf ? (
                <TotalBanner $blue>
                  <span>Réel perçu</span>
                  <span>
                    {montant(order.ca_reel_chf)}
                    {ecart !== 0 && (
                      <span style={{ fontFamily: t.font, fontSize: '0.76rem', fontWeight: 500, opacity: 0.75, marginLeft: 8 }}>
                        ({montant(ecart, { signe: true })})
                      </span>
                    )}
                  </span>
                </TotalBanner>
              ) : null}
            </Panel>
          </Stagger>
        </div>
      </Row>

      {/* ── Coût de revient ──
          Une commande saisie à la main n'a pas de devis : elle porte
          donc son propre calcul, sans quoi elle resterait absente des
          statistiques de marge. */}
      <Stagger $i={5} style={{ marginTop: 16 }}>
        <CoutLivraison
          destination={destinationClient(order.clients)}
          lignesDevis={(order.lignes || []).map((l) => ({
            produit: l.produit, poids: l.poids_t, taxe: l.taxe_100kg,
          }))}
          vente={order.ca_compta_chf ?? order.ca_total_chf ?? 0}
          cible={{ table: 'orders', id: order.id }}
          coutEnregistre={order.cout_revient}
          onEnregistre={(c) => setOrder((o) => ({ ...o, cout_revient: c }))}
        />
      </Stagger>
    </>
  )
}
